import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminProductsPage.css';
import { log, logError } from '../../utils/logger';
import { formatARS } from '../../utils/currency';
import { invalidateStoreSettingsCache } from '../../hooks/useStoreSettings';

const StatsCard = ({ icon, label, value, variant }) => (
  <div className="APP-stat-card">
    <div className={classNames('APP-stat-icon', `APP-stat-icon--${variant}`)}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </div>
    <div className="APP-stat-content">
      <div className="APP-stat-value">{value}</div>
      <div className="APP-stat-label">{label}</div>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="APP-empty-state">
    <i className="bi-cart APP-empty-icon" />
    <h5 className="APP-empty-title">Aún no hay productos</h5>
    <p className="APP-empty-description">
      Los productos se crean automáticamente al activar cursos, eventos o items.
    </p>
  </div>
);

const AdminProductsPageInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos
  const hasViewPanel = useMemo(() => hasPermission(user, PERMISSION_IDS.PANEL_PRODUCTOS), [user]);
  const hasViewProducts = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_PRODUCTOS), [user]);
  const hasCreateProducts = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_PRODUCTOS), [user]);
  const hasEditProducts = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_PRODUCTOS), [user]);
  const hasViewBundles = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_BUNDLES), [user]);
  const hasCreateBundles = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_BUNDLES), [user]);
  const hasEditBundles = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_BUNDLES), [user]);

  // Si no tiene permiso, redirigir
  useEffect(() => {
    if (user && !hasViewPanel && !hasViewProducts) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de productos.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewPanel, hasViewProducts, navigate, notify]);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    draft: 0,
    byType: { courses: 0, events: 0, items: 0, consults: 0 }
  });
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Modal para activar como producto
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activatePrice, setActivatePrice] = useState('');
  const [activateDescription, setActivateDescription] = useState('');
  const [activating, setActivating] = useState(false);
  
  // Modal para editar precio
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [selectedProductForPrice, setSelectedProductForPrice] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [updatingPrice, setUpdatingPrice] = useState(false);
  
  // Bundles section
  const [expandedBundles, setExpandedBundles] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [bundleStats, setBundleStats] = useState({ total: 0, active: 0, inactive: 0 });
  
  // Modal para crear/editar bundle
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [bundleFormData, setBundleFormData] = useState({
    nombre: '',
    descripcion: '',
    price: '',
    discountPercent: '',
    status: 'inactive'
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productQuantities, setProductQuantities] = useState({});
  const [productSearch, setProductSearch] = useState('');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [savingBundle, setSavingBundle] = useState(false);
  const [showShippingSettings, setShowShippingSettings] = useState(false);
  const [freeShippingAmount, setFreeShippingAmount] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [savingShipping, setSavingShipping] = useState(false);
  const [loadingShipping, setLoadingShipping] = useState(false);

  const fetchStats = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingStats(true);
    try {
      const response = await fetch(buildApiUrl('/api/products/stats'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error al cargar estadísticas (${response.status})`);
      }
      const data = await response.json();
      setStats(data.stats || data);
    } catch (error) {
      logError('[AdminProductsPage] Error fetching stats:', error);
      notify({
        title: 'No pudimos cargar las estadísticas',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchProducts = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingProducts(true);
    try {
      const url = buildApiUrl(`/api/products`);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error al cargar los productos (${response.status})`);
      }
      const data = await response.json();
      const productsList = Array.isArray(data.products) ? data.products : [];
      log('[AdminProductsPage] Products loaded:', productsList.length);
      log('[AdminProductsPage] Pending items:', productsList.filter(p => p.status === 'pending' || !p.isProduct));
      setProducts(productsList);
    } catch (error) {
      logError('[AdminProductsPage] Error fetching products:', error);
      notify({
        title: 'No pudimos cargar los productos',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchBundles = async () => {
    if (!token || !hasViewBundles) return;
    setLoadingBundles(true);
    try {
      const response = await fetch(buildApiUrl('/api/bundles'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error al cargar bundles (${response.status})`);
      }
      const data = await response.json();
      setBundles(Array.isArray(data.bundles) ? data.bundles : []);
    } catch (error) {
      logError('[AdminProductsPage] Error fetching bundles:', error);
      notify({
        title: 'Error',
        message: 'No se pudieron cargar los bundles',
        variant: 'error',
      });
    } finally {
      setLoadingBundles(false);
    }
  };

  const fetchBundleStats = async () => {
    if (!token || !hasViewBundles) return;
    try {
      const response = await fetch(buildApiUrl('/api/bundles/stats'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error al cargar estadísticas de bundles (${response.status})`);
      }
      const data = await response.json();
      setBundleStats(data.stats || { total: 0, active: 0, inactive: 0 });
    } catch (error) {
      logError('[AdminProductsPage] Error fetching bundle stats:', error);
    }
  };

  const fetchShippingSettings = async () => {
    if (!token) return;
    setLoadingShipping(true);
    try {
      const response = await fetch(buildApiUrl('/api/store-settings'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('No se pudo cargar la configuración de envío');
      const data = await response.json();
      const settings = data.settings || {};
      setFreeShippingAmount(
        settings.freeShippingThreshold != null ? String(settings.freeShippingThreshold) : '',
      );
      setWhatsappPhone(settings.whatsappPhone || '');
    } catch (error) {
      logError('[AdminProductsPage] store-settings:', error);
    } finally {
      setLoadingShipping(false);
    }
  };

  const handleSaveShippingSettings = async () => {
    if (!hasEditProducts) {
      notify({
        title: 'Sin permiso',
        message: 'Necesitás permiso para editar productos.',
        variant: 'error',
      });
      return;
    }
    setSavingShipping(true);
    try {
      const response = await fetch(buildApiUrl('/api/store-settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          freeShippingThreshold: freeShippingAmount === '' ? null : Number(freeShippingAmount),
          whatsappPhone: whatsappPhone.trim() || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'No se pudo guardar');
      }
      invalidateStoreSettingsCache();
      const settings = data.settings || {};
      setFreeShippingAmount(
        settings.freeShippingThreshold != null ? String(settings.freeShippingThreshold) : '',
      );
      setWhatsappPhone(settings.whatsappPhone || '');
      notify({
        title: 'Guardado',
        message: 'La aclaración de envío y el WhatsApp se actualizaron.',
        variant: 'success',
      });
    } catch (error) {
      notify({
        title: 'Error',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setSavingShipping(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchStats();
      fetchProducts();
      fetchShippingSettings();
      if (hasViewBundles) {
        fetchBundles();
        fetchBundleStats();
      }
    }
  }, [token, hasViewBundles]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    const promises = [fetchStats(true), fetchProducts(true), fetchShippingSettings()];
    if (hasViewBundles) {
      promises.push(fetchBundles(), fetchBundleStats());
    }
    await Promise.all(promises);
    setIsRefreshing(false);
  };

  const handleOpenBundleModal = (bundle = null) => {
    if (bundle) {
      // Editar bundle existente
      setEditingBundle(bundle);
      setBundleFormData({
        nombre: bundle.nombre || '',
        descripcion: bundle.descripcion || '',
        price: bundle.price?.toString() || '',
        discountPercent: bundle.discountPercent?.toString() || '0',
        status: bundle.status || 'inactive'
      });
      // Cargar productos del bundle
      const bundleProducts = bundle.products || [];
      setSelectedProducts(bundleProducts.map(p => p.id));
      const quantities = {};
      bundleProducts.forEach(p => {
        quantities[p.id] = p.ProductBundleItem?.quantity || 1;
      });
      setProductQuantities(quantities);
    } else {
      // Crear nuevo bundle
      setEditingBundle(null);
      setBundleFormData({
        nombre: '',
        descripcion: '',
        price: '',
        discountPercent: '',
        status: 'inactive'
      });
      setSelectedProducts([]);
      setProductQuantities({});
    }
    // Cargar productos disponibles (solo activos)
    const activeProducts = products.filter(p => p.isProduct && p.status === 'active');
    setAvailableProducts(activeProducts);
    setProductSearch('');
    setShowBundleModal(true);
  };

  const handleSaveBundle = async () => {
    if (!bundleFormData.nombre || !bundleFormData.price) {
      notify({
        title: 'Datos incompletos',
        message: 'El nombre y el precio son requeridos',
        variant: 'error',
      });
      return;
    }

    if (selectedProducts.length === 0) {
      notify({
        title: 'Productos requeridos',
        message: 'Debes seleccionar al menos un producto para el bundle',
        variant: 'error',
      });
      return;
    }

    setSavingBundle(true);
    try {
      const bundleData = {
        nombre: bundleFormData.nombre.trim(),
        descripcion: bundleFormData.descripcion.trim() || null,
        price: parseFloat(bundleFormData.price),
        discountPercent: bundleFormData.discountPercent ? parseFloat(bundleFormData.discountPercent) : 0,
        status: bundleFormData.status,
        products: selectedProducts.map(productId => ({
          productId: productId,
          quantity: productQuantities[productId] || 1
        }))
      };

      const url = editingBundle 
        ? buildApiUrl(`/api/bundles/${editingBundle.id}`)
        : buildApiUrl('/api/bundles');
      const method = editingBundle ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bundleData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al ${editingBundle ? 'actualizar' : 'crear'} bundle (${response.status})`);
      }

      notify({
        title: 'Éxito',
        message: `Bundle ${editingBundle ? 'actualizado' : 'creado'} exitosamente`,
        variant: 'success',
      });

      setShowBundleModal(false);
      setEditingBundle(null);
      await fetchBundles();
      await fetchBundleStats();
    } catch (error) {
      logError('[AdminProductsPage] Error saving bundle:', error);
      notify({
        title: 'Error',
        message: error.message || `No se pudo ${editingBundle ? 'actualizar' : 'crear'} el bundle`,
        variant: 'error',
      });
    } finally {
      setSavingBundle(false);
    }
  };

  const filteredAvailableProducts = useMemo(() => {
    if (!productSearch.trim()) {
      return availableProducts;
    }
    const term = productSearch.trim().toLowerCase();
    return availableProducts.filter(p => 
      (p.name || '').toLowerCase().includes(term) ||
      (p.description || '').toLowerCase().includes(term) ||
      (p.sku || '').toLowerCase().includes(term)
    );
  }, [availableProducts, productSearch]);

  const toggleProductSelection = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
      const newQuantities = { ...productQuantities };
      delete newQuantities[productId];
      setProductQuantities(newQuantities);
    } else {
      setSelectedProducts([...selectedProducts, productId]);
      setProductQuantities({
        ...productQuantities,
        [productId]: 1
      });
    }
  };

  const handleActivateAsProduct = (item) => {
    setSelectedItem(item);
    setActivatePrice(item.price || '0.00');
    setActivateDescription(item.description || '');
    setShowActivateModal(true);
  };

  const handleEditPrice = (product) => {
    setSelectedProductForPrice(product);
    setEditPrice(product.price?.toString() || '0.00');
    setShowPriceModal(true);
  };

  const handleUpdatePrice = async () => {
    if (!selectedProductForPrice) return;
    
    const price = parseFloat(editPrice);
    if (isNaN(price) || price < 0) {
      notify({
        title: 'Precio inválido',
        message: 'El precio debe ser un número mayor o igual a 0',
        variant: 'error',
      });
      return;
    }

    setUpdatingPrice(true);
    try {
      const response = await fetch(buildApiUrl(`/api/products/${selectedProductForPrice.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          price: price,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al actualizar precio (${response.status})`);
      }

      notify({
        title: 'Éxito',
        message: 'Precio actualizado exitosamente',
        variant: 'success',
      });

      setShowPriceModal(false);
      setSelectedProductForPrice(null);
      setEditPrice('');
      
      // Recargar productos
      await fetchProducts(true);
      await fetchStats(true);
    } catch (error) {
      logError('[AdminProductsPage] Error updating price:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo actualizar el precio',
        variant: 'error',
      });
    } finally {
      setUpdatingPrice(false);
    }
  };

  const handleToggleProductStatus = async (productId, currentStatus) => {
    if (!hasCreateProducts) {
      notify({
        title: 'Permisos insuficientes',
        message: 'No tienes permisos para cambiar el estado de productos.',
        variant: 'error',
      });
      return;
    }

    const newStatus = currentStatus === 'draft' ? 'active' : currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      const response = await fetch(buildApiUrl(`/api/products/${productId}/toggle-status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al cambiar estado (${response.status})`);
      }

      notify({
        title: 'Éxito',
        message: `Producto ${newStatus === 'active' ? 'activado' : 'desactivado'} exitosamente`,
        variant: 'success',
      });

      // Recargar productos
      await fetchProducts(true);
      await fetchStats(true);
    } catch (error) {
      logError('[AdminProductsPage] Error toggling product status:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo cambiar el estado del producto',
        variant: 'error',
      });
    }
  };

  const handleConfirmActivate = async () => {
    if (!selectedItem) return;
    
    const price = parseFloat(activatePrice);
    if (isNaN(price) || price < 0) {
      notify({
        title: 'Precio inválido',
        message: 'El precio debe ser un número mayor o igual a 0',
        variant: 'error',
      });
      return;
    }

    setActivating(true);
    try {
      let url, body;
      
      if (selectedItem.type === 'course') {
        url = buildApiUrl('/api/products/activate-course');
        body = {
          courseId: selectedItem.referenceId,
          price: price,
          description: activateDescription || undefined,
          status: 'active', // Activar directamente como activo
        };
      } else if (selectedItem.type === 'event') {
        url = buildApiUrl(`/api/products/events/${selectedItem.referenceId}/activate`);
        body = {
          price: price,
          description: activateDescription || undefined,
          status: 'active', // Activar directamente como activo
        };
      } else if (selectedItem.type === 'item') {
        // Para items, usar el endpoint de toggleItemActive
        url = buildApiUrl(`/api/items/${selectedItem.referenceId}/activate`);
        body = {
          isActive: true,
          productData: {
            name: selectedItem.name,
            price: price,
            description: activateDescription || undefined,
            status: 'active', // Activar directamente como activo
          },
        };
      } else {
        throw new Error('Tipo de elemento no soportado');
      }

      const response = await fetch(url, {
        method: selectedItem.type === 'item' ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al activar (${response.status})`);
      }

      notify({
        title: 'Éxito',
        message: 'Elemento activado como producto activo exitosamente',
        variant: 'success',
      });

      setShowActivateModal(false);
      setSelectedItem(null);
      setActivatePrice('');
      setActivateDescription('');
      
      // Recargar productos
      await fetchProducts(true);
      await fetchStats(true);
    } catch (error) {
      logError('[AdminProductsPage] Error activating product:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo activar el elemento como producto',
        variant: 'error',
      });
    } finally {
      setActivating(false);
    }
  };

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Filtrar por tipo
    if (typeFilter !== 'all') {
      filtered = filtered.filter((product) => {
        const type = product.type || product.productType;
        if (typeFilter === 'course') return type === 'course';
        if (typeFilter === 'event') return type === 'event';
        if (typeFilter === 'item') return ['item', 'items', 'physical'].includes(type);
        if (typeFilter === 'consult') return type === 'consult';
        if (typeFilter === 'pending') return product.status === 'pending' || !product.isProduct;
        return true;
      });
    }

    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter((product) => {
        if (product.status === 'pending' || !product.isProduct) {
          return statusFilter === 'pending';
        }
        return product.status === statusFilter;
      });
    }

    // Filtrar por búsqueda
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      filtered = filtered.filter((product) => {
        const name = product.name || '';
        const description = product.description || '';
        return (
          name.toLowerCase().includes(term) ||
          description.toLowerCase().includes(term) ||
          String(product.id || product.referenceId).includes(term) ||
          (product.sku && product.sku.toLowerCase().includes(term))
        );
      });
    }
    
    return filtered;
  }, [products, typeFilter, statusFilter, search]);

  const getProductTypeLabel = (type) => {
    const labels = {
      course: 'Curso',
      event: 'Evento',
      item: 'Item',
      items: 'Item',
      physical: 'Item',
      consult: 'Asesoría'
    };
    return labels[type] || type;
  };

  const getProductTypeIcon = (type) => {
    const icons = {
      course: 'bi-mortarboard-fill',
      event: 'bi-calendar-event-fill',
      item: 'bi-box-seam-fill',
      items: 'bi-box-seam-fill',
      physical: 'bi-box-seam-fill',
      consult: 'bi-person-video3-fill'
    };
    return icons[type] || 'bi-cart-fill';
  };

  const getStatusBadge = (product) => {
    if (product.status === 'pending' || !product.isProduct) {
      return { label: 'Pendiente', variant: 'warning' };
    }
    const badges = {
      active: { label: 'Activo', variant: 'success' },
      inactive: { label: 'Inactivo', variant: 'secondary' },
      draft: { label: 'Borrador', variant: 'warning' }
    };
    return badges[product.status] || { label: product.status, variant: 'secondary' };
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="products"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="APP-container">
            <div className="APP-header">
              <div className="APP-header-content">
                <h1 className="APP-title">
                  <i className="bi-cart-fill text-primary" />
                  Gestión de Productos
                </h1>
                <p className="APP-subtitle">
                  Administra todos tus productos: cursos, eventos, items y asesorías en un solo lugar.
                </p>
              </div>
              <div className="APP-actions">
                <Button
                  variant="outline-success"
                  onClick={() => setShowShippingSettings((open) => !open)}
                >
                  <i className="bi-truck me-2" />
                  Envío gratis a partir de:
                </Button>
                <Button variant="primary" onClick={handleRefresh} disabled={isRefreshing}>
                  {isRefreshing ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Actualizando
                    </>
                  ) : (
                    <>
                      <i className="bi-arrow-repeat me-2" />
                      Actualizar
                    </>
                  )}
                </Button>
              </div>
            </div>

            {showShippingSettings && (
              <section className="APP-shipping-panel">
                <h2 className="APP-shipping-title">Envíos coordinados por WhatsApp</h2>
                <p className="APP-shipping-help">
                  El monto se muestra en la tienda como “Envío gratis a partir de”. El número se usa
                  en botones y correos para que el comprador coordine la entrega.
                </p>
                {loadingShipping ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <Form
                    className="APP-shipping-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveShippingSettings();
                    }}
                  >
                    <Form.Group className="APP-shipping-field">
                      <Form.Label>Envío gratis a partir de ($)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Ej: 50000"
                        value={freeShippingAmount}
                        onChange={(e) => setFreeShippingAmount(e.target.value)}
                        disabled={!hasEditProducts}
                      />
                      <Form.Text>
                        Dejá vacío para no mostrar el umbral. Cero también lo oculta.
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="APP-shipping-field">
                      <Form.Label>WhatsApp de coordinación</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="Ej: 11 1234-5678 o +54 9 11..."
                        value={whatsappPhone}
                        onChange={(e) => setWhatsappPhone(e.target.value)}
                        disabled={!hasEditProducts}
                      />
                      <Form.Text>
                        Sin este número no aparecen los botones de WhatsApp en web ni en mails.
                      </Form.Text>
                    </Form.Group>
                    {hasEditProducts && (
                      <Button type="submit" variant="success" disabled={savingShipping}>
                        {savingShipping ? 'Guardando…' : 'Guardar'}
                      </Button>
                    )}
                  </Form>
                )}
              </section>
            )}

            <section className="APP-stats-section">
              <div className="APP-stats-grid">
                <StatsCard
                  icon="bi-cart-fill"
                  label="Total productos"
                  value={loadingStats ? '--' : stats.total}
                  variant="primary"
                />
                <StatsCard
                  icon="bi-check-circle-fill"
                  label="Activos"
                  value={loadingStats ? '--' : stats.active}
                  variant="success"
                />
                <StatsCard
                  icon="bi-pause-circle-fill"
                  label="Inactivos"
                  value={loadingStats ? '--' : stats.inactive}
                  variant="warning"
                />
                <StatsCard
                  icon="bi-file-earmark-text-fill"
                  label="Borradores"
                  value={loadingStats ? '--' : stats.draft}
                  variant="muted"
                />
              </div>
            </section>

            <section className="APP-filters-section">
              <div className="APP-filters-card">
                <div className="APP-filters-content">
                  <Form.Control
                    type="search"
                    placeholder="Buscar por nombre, descripción, SKU o ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="APP-search-input"
                  />
                  <Form.Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="APP-filter-select"
                  >
                    <option value="all">Todos los tipos</option>
                    <option value="course">Cursos</option>
                    <option value="event">Eventos</option>
                    <option value="item">Items</option>
                    <option value="consult">Asesorías</option>
                    <option value="pending">Pendientes de activar</option>
                  </Form.Select>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="APP-filter-select"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activos</option>
                    <option value="inactive">Inactivos</option>
                    <option value="draft">Borradores</option>
                    <option value="pending">Pendientes</option>
                  </Form.Select>
                  {search && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearch('')}
                      size="sm"
                    >
                      Limpiar
                    </Button>
                  )}
                </div>
              </div>
            </section>

            <section className="APP-products-section">
              {loadingProducts ? (
                <div className="APP-loading">
                  <Spinner animation="border" variant="primary" />
                  <p>Cargando productos...</p>
                </div>
              ) : filteredProducts.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="APP-table-container">
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th>Nombre</th>
                        <th>Descripción</th>
                        <th>Precio</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => {
                        const type = product.type || product.productType || 'item';
                        const typeLabel = getProductTypeLabel(type);
                        const typeIcon = getProductTypeIcon(type);
                        const statusBadge = getStatusBadge(product);
                        // Verificar si es pendiente: tiene status 'pending' O no tiene isProduct o isProduct es false
                        const isPending = product.status === 'pending' || product.isProduct === false || product.isProduct === undefined;
                        
                        // Generar una clave única para evitar duplicados
                        const uniqueKey = product.isProduct 
                          ? `product-${product.id}` 
                          : `${product.type || 'pending'}-${product.referenceId}`;

                        return (
                          <tr key={uniqueKey}>
                            <td>
                              <Badge className={`APP-type-badge APP-type-badge-${type}`}>
                                <i className={`bi ${typeIcon} me-1`} />
                                {typeLabel}
                              </Badge>
                            </td>
                            <td>
                              <strong>{product.name || 'Sin nombre'}</strong>
                            </td>
                            <td>
                              <span className="APP-description-cell">
                                {product.description || 'Sin descripción'}
                              </span>
                            </td>
                            <td>
                              {isPending ? (
                                <span className="text-muted">--</span>
                              ) : (
                                <strong className="text-success">
                                  {formatARS(parseFloat(product.price || 0))}
                                </strong>
                              )}
                            </td>
                            <td>
                              <Badge bg={statusBadge.variant}>
                                {statusBadge.label}
                              </Badge>
                            </td>
                            <td>
                              <div className="APP-actions-cell">
                                {isPending && (
                                  <button
                                    className="APP-btn-activate"
                                    onClick={() => {
                                      if (hasCreateProducts) {
                                        handleActivateAsProduct(product);
                                      } else {
                                        notify({
                                          title: 'Permisos insuficientes',
                                          message: 'No tienes permisos para activar productos.',
                                          variant: 'error',
                                        });
                                      }
                                    }}
                                    title="Activar como producto"
                                  >
                                    <i className="bi-check-circle" />
                                    <span>Activar</span>
                                  </button>
                                )}
                                {!isPending && product.status === 'draft' && hasEditProducts && (
                                  <button
                                    className="APP-btn-activate-draft"
                                    onClick={() => handleToggleProductStatus(product.id, product.status)}
                                    title="Activar producto (cambiar de borrador a activo)"
                                  >
                                    <i className="bi-check-circle-fill" />
                                    <span>Activar</span>
                                  </button>
                                )}
                                {!isPending && product.status === 'active' && hasEditProducts && (
                                  <button
                                    className="APP-btn-deactivate"
                                    onClick={() => handleToggleProductStatus(product.id, product.status)}
                                    title="Desactivar producto"
                                  >
                                    <i className="bi-pause-circle-fill" />
                                    <span>Desactivar</span>
                                  </button>
                                )}
                                {!isPending && product.status === 'inactive' && hasEditProducts && (
                                  <button
                                    className="APP-btn-activate"
                                    onClick={() => handleToggleProductStatus(product.id, product.status)}
                                    title="Activar producto"
                                  >
                                    <i className="bi-play-circle-fill" />
                                    <span>Activar</span>
                                  </button>
                                )}
                                {!isPending && hasEditProducts && (
                                  <button
                                    className="APP-btn-price"
                                    onClick={() => handleEditPrice(product)}
                                    title="Editar precio del producto"
                                  >
                                    <i className="bi-cash-coin" />
                                    <span>Precio</span>
                                  </button>
                                )}
                                <button
                                  className="APP-btn-detail"
                                  onClick={() => {
                                    const type = product.type || product.productType;
                                    const refId = product.referenceId || product.id;
                                    if (type === 'course') {
                                      navigate(`/admin/courses/${refId}`);
                                    } else if (type === 'event') {
                                      navigate(`/admin/events/${refId}`);
                                    } else if (['item', 'items', 'physical'].includes(type)) {
                                      navigate(`/admin/items/${refId}`);
                                    } else {
                                      navigate(`/admin/products/${product.id}`);
                                    }
                                  }}
                                  title="Ver detalle"
                                >
                                  <i className="bi-box-arrow-in-right" />
                                  <span>Ver detalle</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </section>

            {/* Sección de Bundles */}
            {hasViewBundles && (
              <section className="APP-bundles-section">
                <div className="APP-bundles-container">
                  <div className="APP-bundles-header" onClick={() => setExpandedBundles(!expandedBundles)}>
                    <div>
                      <h2 className="APP-bundles-title">
                        <i className={`bi-chevron-${expandedBundles ? 'down' : 'right'} APP-bundles-chevron`} />
                        Bundles de Productos
                      </h2>
                      {expandedBundles && (
                        <p className="APP-bundles-subtitle">
                          Combina múltiples productos en paquetes con descuentos especiales.
                        </p>
                      )}
                    </div>
                    {expandedBundles && hasCreateBundles && (
                      <Button 
                        variant="primary" 
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          handleOpenBundleModal(); 
                        }}
                      >
                        <i className="bi-plus-circle me-2" />
                        Crear Bundle
                      </Button>
                    )}
                  </div>
                  {expandedBundles && (
                    <div className="APP-bundles-content">
                      {/* Estadísticas de bundles */}
                      <div className="APP-bundles-stats">
                        <div className="APP-bundles-stat-item">
                          <span className="APP-bundles-stat-label">Total:</span>
                          <span className="APP-bundles-stat-value">{bundleStats.total}</span>
                        </div>
                        <div className="APP-bundles-stat-item">
                          <span className="APP-bundles-stat-label">Activos:</span>
                          <span className="APP-bundles-stat-value text-success">{bundleStats.active}</span>
                        </div>
                        <div className="APP-bundles-stat-item">
                          <span className="APP-bundles-stat-label">Inactivos:</span>
                          <span className="APP-bundles-stat-value text-muted">{bundleStats.inactive}</span>
                        </div>
                      </div>

                      {/* Tabla de bundles */}
                      {loadingBundles ? (
                        <div className="APP-loading">
                          <Spinner animation="border" variant="primary" />
                          <p>Cargando bundles...</p>
                        </div>
                      ) : bundles.length === 0 ? (
                        <div className="APP-empty-state">
                          <i className="bi-boxes APP-empty-icon" />
                          <h5 className="APP-empty-title">Aún no hay bundles</h5>
                          <p className="APP-empty-description">
                            Crea tu primer bundle combinando múltiples productos con descuentos especiales.
                          </p>
                        </div>
                      ) : (
                        <div className="APP-table-container">
                          <Table striped bordered hover responsive>
                            <thead>
                              <tr>
                                <th>Nombre</th>
                                <th>Descripción</th>
                                <th>Precio</th>
                                <th>Descuento</th>
                                <th>Productos</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bundles.map((bundle) => (
                                <tr key={bundle.id}>
                                  <td><strong>{bundle.nombre}</strong></td>
                                  <td>
                                    <span className="APP-description-cell">
                                      {bundle.descripcion || 'Sin descripción'}
                                    </span>
                                  </td>
                                  <td>
                                    <strong className="text-success">
                                      {formatARS(parseFloat(bundle.price || 0))}
                                    </strong>
                                  </td>
                                  <td>
                                    {bundle.discountPercent > 0 ? (
                                      <Badge bg="info">-{bundle.discountPercent}%</Badge>
                                    ) : (
                                      <span className="text-muted">Sin descuento</span>
                                    )}
                                  </td>
                                  <td>
                                    <Badge bg="secondary">
                                      {bundle.productsCount || bundle.products?.length || 0} productos
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg={bundle.status === 'active' ? 'success' : 'secondary'}>
                                      {bundle.status === 'active' ? 'Activo' : 'Inactivo'}
                                    </Badge>
                                  </td>
                                  <td>
                                    <div className="APP-actions-cell">
                                      {hasEditBundles && (
                                        <button
                                          className="APP-btn-edit"
                                          onClick={() => handleOpenBundleModal(bundle)}
                                          title="Editar bundle"
                                        >
                                          <i className="bi-pencil" />
                                          <span>Editar</span>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </main>
      </div>

      {/* Modal para activar como producto */}
      <Modal show={showActivateModal} onHide={() => !activating && setShowActivateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-check-circle me-2" />
            Activar como producto
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <>
              <div className="mb-3">
                <strong>Elemento:</strong> {selectedItem.name}
              </div>
              <div className="mb-3">
                <strong>Tipo:</strong> {getProductTypeLabel(selectedItem.type)}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>
                  Precio <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={activatePrice}
                  onChange={(e) => setActivatePrice(e.target.value)}
                  placeholder="0.00"
                  disabled={activating}
                />
                <Form.Text className="text-muted">
                  Ingresa el precio del producto en pesos argentinos (ARS)
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={activateDescription}
                  onChange={(e) => setActivateDescription(e.target.value)}
                  placeholder="Descripción del producto (opcional)"
                  disabled={activating}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowActivateModal(false)}
            disabled={activating}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmActivate}
            disabled={activating || !activatePrice}
          >
            {activating ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Activando...
              </>
            ) : (
              <>
                <i className="bi-check-circle me-2" />
                Activar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para editar precio */}
      <Modal show={showPriceModal} onHide={() => !updatingPrice && setShowPriceModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-cash-coin me-2" />
            Editar Precio
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProductForPrice && (
            <>
              <div className="mb-3">
                <strong>Producto:</strong> {selectedProductForPrice.name}
              </div>
              <div className="mb-3">
                <strong>Precio actual:</strong> {formatARS(parseFloat(selectedProductForPrice.price || 0))}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>
                  Nuevo Precio <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  placeholder="0.00"
                  disabled={updatingPrice}
                />
                <Form.Text className="text-muted">
                  Ingresa el nuevo precio del producto en pesos argentinos (ARS)
                </Form.Text>
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPriceModal(false)}
            disabled={updatingPrice}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdatePrice}
            disabled={updatingPrice || !editPrice}
          >
            {updatingPrice ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Actualizando...
              </>
            ) : (
              <>
                <i className="bi-check-circle me-2" />
                Actualizar Precio
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para crear/editar bundle */}
      <Modal 
        show={showBundleModal} 
        onHide={() => !savingBundle && setShowBundleModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-boxes me-2" />
            {editingBundle ? 'Editar Bundle' : 'Crear Bundle'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                Nombre del Bundle <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={bundleFormData.nombre}
                onChange={(e) => setBundleFormData({ ...bundleFormData, nombre: e.target.value })}
                placeholder="Ej: Paquete Premium"
                disabled={savingBundle}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={bundleFormData.descripcion}
                onChange={(e) => setBundleFormData({ ...bundleFormData, descripcion: e.target.value })}
                placeholder="Descripción del bundle..."
                disabled={savingBundle}
              />
            </Form.Group>

            <div className="row">
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>
                  Precio <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={bundleFormData.price}
                  onChange={(e) => setBundleFormData({ ...bundleFormData, price: e.target.value })}
                  placeholder="0.00"
                  disabled={savingBundle}
                />
              </Form.Group>

              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Descuento (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={bundleFormData.discountPercent}
                  onChange={(e) => setBundleFormData({ ...bundleFormData, discountPercent: e.target.value })}
                  placeholder="0"
                  disabled={savingBundle}
                />
              </Form.Group>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={bundleFormData.status}
                onChange={(e) => setBundleFormData({ ...bundleFormData, status: e.target.value })}
                disabled={savingBundle}
              >
                <option value="inactive">Inactivo</option>
                <option value="active">Activo</option>
              </Form.Select>
            </Form.Group>

            <hr />

            <Form.Group className="mb-3">
              <Form.Label>
                Seleccionar Productos <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Buscar productos por nombre, descripción o SKU..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="mb-3"
                disabled={savingBundle}
              />
              
              <div className="APP-products-selection" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem', padding: '0.5rem' }}>
                {filteredAvailableProducts.length === 0 ? (
                  <p className="text-muted text-center mb-0">No hay productos disponibles</p>
                ) : (
                  filteredAvailableProducts.map((product) => {
                    const isSelected = selectedProducts.includes(product.id);
                    return (
                      <div 
                        key={product.id} 
                        className={`APP-product-selection-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => !savingBundle && toggleProductSelection(product.id)}
                        style={{
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          border: `2px solid ${isSelected ? '#2563eb' : '#dee2e6'}`,
                          borderRadius: '0.375rem',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#eff6ff' : 'white',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2">
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <div>
                                <strong>{product.name}</strong>
                                <div className="text-muted small">
                                  {formatARS(parseFloat(product.price || 0))} • {getProductTypeLabel(product.type)}
                                </div>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <Form.Control
                              type="number"
                              min="1"
                              value={productQuantities[product.id] || 1}
                              onChange={(e) => {
                                const qty = parseInt(e.target.value) || 1;
                                setProductQuantities({
                                  ...productQuantities,
                                  [product.id]: qty
                                });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '80px' }}
                              disabled={savingBundle}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {selectedProducts.length > 0 && (
                <div className="mt-2">
                  <small className="text-muted">
                    {selectedProducts.length} producto(s) seleccionado(s)
                  </small>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBundleModal(false)}
            disabled={savingBundle}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveBundle}
            disabled={savingBundle || !bundleFormData.nombre || !bundleFormData.price || selectedProducts.length === 0}
          >
            {savingBundle ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {editingBundle ? 'Actualizando...' : 'Creando...'}
              </>
            ) : (
              <>
                <i className="bi-check-circle me-2" />
                {editingBundle ? 'Actualizar Bundle' : 'Crear Bundle'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminProductsPage = () => (
  <AdminNotificationsProvider>
    <AdminProductsPageInner />
  </AdminNotificationsProvider>
);

export default AdminProductsPage;
